/**
 * NotificationLogsRepository - 알림 이력 데이터 접근 계층
 * 백엔드 API와 통신하여 알림 발송 이력 조회
 */

import { API_BASE_URL } from '../../../config/api';
import { ALIMTALK_TYPE_LABELS } from '../../../shared/constants/alimtalkTypeLabels';

export interface NotificationLog {
  id: string;
  notification_type: string; // 'gate_entry_exit' | 'ticket_expiry' | 'study_time_report' | 'manual'
  template_code: string;
  user_id: string;
  user_name: string;
  phone: string;
  message: string;
  status: 'success' | 'failed' | 'pending';
  sent_at: string; // ISO 8601 timestamp
  metadata: {
    gate_id?: number;
    action?: string; // 'entry' | 'exit'
    seat_number?: number;
    [key: string]: any;
  };
}

export interface NotificationLogsResponse {
  logs: NotificationLog[];
  total: number;
  limit: number;
  offset: number;
}

export interface NotificationStats {
  total_count: number;
  success_count: number;
  failed_count: number;
  success_rate: number;
  type_stats: {
    [key: string]: number;
  };
  period_days: number;
}

export class NotificationLogsRepository {
  /**
   * 알림 이력 목록 조회
   */
  async getNotificationLogs(params: {
    notification_type?: string;
    days?: number;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<NotificationLogsResponse> {
    const queryParams = new URLSearchParams();

    if (params.notification_type) queryParams.append('notification_type', params.notification_type);
    if (params.days) queryParams.append('days', params.days.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());

    const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
    const response = await fetch(
      `${API_BASE_URL}/api/v1/owner/notification-logs?${queryParams}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`알림 이력 조회 실패: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * 알림 발송 통계 조회
   */
  async getNotificationStats(days: number = 30): Promise<NotificationStats> {
    const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
    const response = await fetch(
      `${API_BASE_URL}/api/v1/owner/notification-logs/stats?days=${days}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`알림 통계 조회 실패: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * 알림 타입 한글 라벨 반환
   */
  /**
   * 알림 종류를 사람이 읽는 말로.
   *
   * 🔴 2026-09-19 — 여기에 5개짜리 목록을 따로 들고 있어서 사유서 승인·스케줄 변경 등이
   *    `exception_apprv_001` 처럼 **코드 그대로** 화면에 나왔다(송파점 제보).
   *    제대로 된 목록은 `shared/constants/alimtalkTypeLabels` 에 이미 있었고 다른 화면
   *    두 곳은 그걸 쓰고 있었다. **목록을 여기에 다시 만들지 말 것** — 한 곳만 고친다.
   *
   * 같은 알림이 종류(gate_entry_exit)와 서식코드(entry_exit_001) 두 이름으로 들어오므로
   * 둘 다 받아서 찾는다.
   */
  getNotificationTypeLabel(type: string, templateCode?: string | null): string {
    if (type && ALIMTALK_TYPE_LABELS[type]) return ALIMTALK_TYPE_LABELS[type];
    if (templateCode && ALIMTALK_TYPE_LABELS[templateCode]) return ALIMTALK_TYPE_LABELS[templateCode];
    return type || templateCode || '알 수 없음';
  }

  /**
   * 알림 상태 한글 라벨 반환
   */
  getStatusLabel(status: string): string {
    const labels: { [key: string]: string } = {
      'success': '발송 성공',
      'failed': '발송 실패',
      'pending': '발송 대기',
    };
    return labels[status] || status;
  }

  /**
   * 알림 상태 스타일 클래스 반환
   */
  getStatusStyle(status: string): string {
    const styles: { [key: string]: string } = {
      'success': 'bg-green-100 text-green-800',
      'failed': 'bg-red-100 text-red-800',
      'pending': 'bg-yellow-100 text-yellow-800',
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  }

  /**
   * 날짜 포맷팅 (ISO 8601 → 한국 시간 표시)
   */
  formatDateTime(isoString: string): string {
    const date = new Date(isoString);

    // 한국 시간대로 변환
    const koreaTime = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));

    const year = koreaTime.getFullYear();
    const month = String(koreaTime.getMonth() + 1).padStart(2, '0');
    const day = String(koreaTime.getDate()).padStart(2, '0');
    const hours = String(koreaTime.getHours()).padStart(2, '0');
    const minutes = String(koreaTime.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}`;
  }
}

// 싱글톤 인스턴스
export const notificationLogsRepository = new NotificationLogsRepository();
